import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Tabs, Table, Spin, Input, Select, Button, Modal, Form, message, Space, DatePicker, Dropdown, Tag, Tooltip, Grid, Divider, Pagination, Radio, Checkbox, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FilterOutlined, ClearOutlined, DownOutlined, RightOutlined, SearchOutlined, CheckOutlined, CloseOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { services } from '../services/api';
import EditPersonaModal from '../components/EditPersonaModal';
import { useAuth } from '../context/AuthContext';

const { Option } = Select;
const { useBreakpoint } = Grid;

const STORAGE_KEY  = 'arbitros_list_state';
const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR     = 1899;

const ORDER_LABELS = { PJ: 'PJ', nombre: 'Nombre' };

const DEFAULT_FILTERS = {
  paisId:             null,
  anioMode:           'rango',
  anioDesde:          MIN_YEAR,
  anioHasta:          CURRENT_YEAR,
  anioUnico:          null,
  equipoId:           null,
  campeonatoNombreId: null,
  soloOficiales:      true,
  orderBy:            'PJ',
};

// URL param key mapping
const FILTER_PARAMS_ARB = {
  paisId:             'pa',
  anioMode:           'am',
  anioDesde:          'ad',
  anioHasta:          'ah',
  anioUnico:          'an',
  equipoId:           'eq',
  campeonatoNombreId: 'nc',
  soloOficiales:      'so',
  orderBy:            'ob',
};
const PARAM_TO_FILTER_ARB = Object.fromEntries(Object.entries(FILTER_PARAMS_ARB).map(([k, v]) => [v, k]));
const INT_FIELDS_ARB = new Set(['anioDesde','anioHasta','anioUnico','equipoId','campeonatoNombreId']);

const filtersToParams_Arb = (filters, search) => {
  const params = {};
  for (const [key, val] of Object.entries(filters)) {
    if (val === undefined || val === null || val === '') continue;
    if (key === 'anioDesde' && val === MIN_YEAR) continue;
    if (key === 'anioHasta' && val === CURRENT_YEAR) continue;
    if (key === 'anioMode' && val === 'rango') continue;
    if (key === 'soloOficiales' && val === true) continue;   // default
    if (key === 'orderBy' && val === 'PJ') continue;
    const p = FILTER_PARAMS_ARB[key];
    if (p) params[p] = String(val);
  }
  if (search && search.trim()) params.bt = search.trim();
  return params;
};

const paramsToFilters_Arb = (searchParams) => {
  const filters = { ...DEFAULT_FILTERS };
  for (const [p, val] of searchParams.entries()) {
    const key = PARAM_TO_FILTER_ARB[p];
    if (!key) continue;
    if (INT_FIELDS_ARB.has(key)) { const n = parseInt(val); if (!isNaN(n)) filters[key] = n; }
    else if (key === 'soloOficiales') filters[key] = val !== '0' && val !== 'false';
    else filters[key] = val;
  }
  return filters;
};

const loadState = () => {
  try { const s = sessionStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
};
const saveState = (state) => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
};

const normalizeText = (t) =>
  (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const formatName = (r) => {
  const apellido = (r.PersonaApellido || '').toUpperCase();
  const nombre = r.PersonaNombre || '';
  return nombre ? `${apellido}, ${nombre}` : apellido;
};

const TABS = [
  { key: '1',  label: 'Árbitro Principal', tipos: [1],    statLabel: 'Árbitro Principal' },
  { key: '23', label: 'Línea',             tipos: [2, 3], statLabel: 'Línea' },
  { key: '4',  label: 'Cuarto Árbitro',    tipos: [4],    statLabel: '4to Árbitro' },
  { key: '5',  label: 'VAR',               tipos: [5],    statLabel: 'VAR' },
  { key: '6',  label: 'Asistente VAR',     tipos: [6],    statLabel: 'Asistente VAR' },
];

const principalExtraColumns = [
  {
    title: 'Amarillas',
    key: 'amarillas',
    width: 90,
    align: 'center',
    render: (_, r) => {
      const n = Number(r.AmarillasNacional) || 0;
      const rv = Number(r.AmarillasRival) || 0;
      if (n === 0 && rv === 0) return '-';
      return <span style={{ color: '#d4b106', fontWeight: 600 }}>{n} / {rv}</span>;
    },
  },
  {
    title: 'Rojas',
    key: 'rojas',
    width: 80,
    align: 'center',
    render: (_, r) => {
      const n = Number(r.ExpulsadosNacional) || 0;
      const rv = Number(r.ExpulsadosRival) || 0;
      if (n === 0 && rv === 0) return '-';
      return <span style={{ color: '#cf1322', fontWeight: 600 }}>{n} / {rv}</span>;
    },
  },
  {
    title: 'Penales',
    key: 'penales',
    width: 80,
    align: 'center',
    render: (_, r) => {
      const n = Number(r.PenalesNacional) || 0;
      const rv = Number(r.PenalesRival) || 0;
      if (n === 0 && rv === 0) return '-';
      return <span style={{ fontWeight: 600 }}>{n} / {rv}</span>;
    },
  },
];

const makeBaseColumns = (onEdit, canEdit) => [
  {
    title: 'Árbitro',
    key: 'nombre',
    render: (_, r) => (
      <span>
        <strong>{(r.PersonaApellido || '').toUpperCase()}</strong>
        {r.PersonaNombre ? `, ${r.PersonaNombre}` : ''}
      </span>
    )
  },
  {
    title: 'País',
    key: 'pais',
    width: 170,
    render: (_, r) => (
      <Space size="small">
        {r.PaisBanderaUrl && <img src={r.PaisBanderaUrl} alt="" style={{ width: 24, height: 16, objectFit: 'cover' }} />}
        <span>{r.PaisNombre}</span>
      </Space>
    )
  },
  {
    title: 'Período',
    key: 'periodo',
    width: 120,
    render: (_, r) => r.AnioPrimero === r.AnioUltimo
      ? String(r.AnioPrimero)
      : `${r.AnioPrimero}-${r.AnioUltimo}`
  },
  { title: 'PJ', dataIndex: 'Partidos', key: 'Partidos', width: 50, align: 'right' },
  { title: 'PG', dataIndex: 'PG', key: 'PG', width: 50, align: 'right' },
  { title: 'PE', dataIndex: 'PE', key: 'PE', width: 50, align: 'right' },
  { title: 'PP', dataIndex: 'PP', key: 'PP', width: 50, align: 'right' },
  {
    title: 'Dif.',
    key: 'dif',
    width: 55,
    align: 'right',
    render: (_, r) => {
      const dif = (r.PG || 0) - (r.PP || 0);
      const color = dif > 0 ? '#52c41a' : dif < 0 ? '#ff4d4f' : undefined;
      return <span style={{ color }}>{dif > 0 ? `+${dif}` : dif}</span>;
    }
  },
  ...(canEdit ? [{
    title: '',
    key: 'actions',
    width: 48,
    render: (_, r) => (
      <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)} />
    )
  }] : [])
];

// ── Tab component ─────────────────────────────────────────────────────────────

const MOBILE_PAGE_SIZE = 50;

const ArbitrosTab = ({ tabKey, tipos, extraColumns = [], refreshKey, onEdit, canEdit, sharedFilters, sharedSearch, onCountChange, isMobile }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobilePage, setMobilePage] = useState(1);
  const navigate = useNavigate();

  const baseColumns = makeBaseColumns(onEdit, canEdit);
  const columns = extraColumns.length > 0
    ? [...baseColumns.slice(0, -1), ...extraColumns, baseColumns[baseColumns.length - 1]]
    : baseColumns;

  const buildParams = useCallback(() => {
    const p = {
      tipos: tipos.join(','),
      search: sharedSearch || undefined,
      orderBy: sharedFilters.orderBy || 'PJ',
    };
    if (sharedFilters.paisId === 'extranjeros') p.paisId = 'extranjeros';
    else if (sharedFilters.paisId) p.paisId = sharedFilters.paisId;
    if (sharedFilters.equipoId) p.equipoId = sharedFilters.equipoId;
    if (sharedFilters.soloOficiales) p.soloOficiales = 'true';
    if (sharedFilters.campeonatoNombreId === 'internacionales') p.campeonatoNombreId = 'internacionales';
    else if (sharedFilters.campeonatoNombreId) p.campeonatoNombreId = sharedFilters.campeonatoNombreId;
    if (sharedFilters.anioMode === 'unico') {
      if (sharedFilters.anioUnico) p.anioUnico = sharedFilters.anioUnico;
    } else {
      if (sharedFilters.anioDesde && sharedFilters.anioDesde !== MIN_YEAR) p.anioDesde = sharedFilters.anioDesde;
      if (sharedFilters.anioHasta && sharedFilters.anioHasta !== CURRENT_YEAR) p.anioHasta = sharedFilters.anioHasta;
    }
    return p;
  }, [tipos, sharedFilters, sharedSearch]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/arbitros', { params: buildParams() })
      .then(r => {
        const rows = r.data.data || [];
        const seen = new Set();
        const deduped = rows.filter(row => {
          if (seen.has(row.JuezId)) return false;
          seen.add(row.JuezId);
          return true;
        });
        setData(deduped);
        onCountChange(tabKey, deduped.length);
      })
      .catch(() => { setData([]); onCountChange(tabKey, 0); })
      .finally(() => setLoading(false));
  }, [buildParams, tabKey, onCountChange]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setMobilePage(1); }, [data]);

  if (isMobile) {
    const pageData = data.slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE);
    return (
      <div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : pageData.map((record) => {
          const dif = (record.PG || 0) - (record.PP || 0);
          const difVal = dif > 0 ? `+${dif}` : dif;
          const difColor = dif > 0 ? '#3f8600' : dif < 0 ? '#cf1322' : '#888';
          const periodo = record.AnioPrimero
            ? (record.AnioPrimero === record.AnioUltimo ? `${record.AnioPrimero}` : `${record.AnioPrimero}–${record.AnioUltimo}`)
            : null;
          return (
            <div
              key={record.JuezId}
              style={{ marginBottom: 8, borderRadius: 10, background: '#fff', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', minHeight: 64 }}
              onClick={() => navigate(`/arbitros/${record.JuezId}`)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 8 }}>
                  {(record.PersonaApellido || '').toUpperCase()}{record.PersonaNombre ? `, ${record.PersonaNombre}` : ''}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {record.PaisBanderaUrl && <img src={record.PaisBanderaUrl} alt="" style={{ width: 24, height: 16, objectFit: 'cover', flexShrink: 0 }} />}
                  {periodo && <span style={{ fontSize: 14, color: '#444' }}>{periodo}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                <div style={{ textAlign: 'center', width: 38 }}>
                  <div style={{ fontSize: 10, color: '#aaa', lineHeight: 1 }}>PJ</div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#262626', lineHeight: 1.3 }}>{record.Partidos ?? '-'}</div>
                </div>
                <div style={{ textAlign: 'center', width: 38 }}>
                  <div style={{ fontSize: 10, color: '#aaa', lineHeight: 1 }}>Dif</div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: difColor, lineHeight: 1.3 }}>{difVal ?? '-'}</div>
                </div>
              </div>
            </div>
          );
        })}
        <Pagination
          current={mobilePage}
          pageSize={MOBILE_PAGE_SIZE}
          total={data.length}
          onChange={(p) => setMobilePage(p)}
          size="small"
          showLessItems
          style={{ textAlign: 'center', marginTop: 8 }}
        />
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="JuezId"
      loading={loading}
      pagination={{ pageSize: 50, showSizeChanger: true }}
      rowClassName="clickable-row"
      onRow={(r) => ({
        onClick: (e) => { if (e.target.closest('.ant-btn')) return; navigate(`/arbitros/${r.JuezId}`); }
      })}
    />
  );
};

// ── Sin Partidos Tab ──────────────────────────────────────────────────────────

const SinPartidosTab = ({ onEdit, onDelete, refreshKey, canEdit, sharedSearch, sharedFilters, onCountChange, isMobile }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobilePage, setMobilePage] = useState(1);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (sharedSearch) params.nombre = sharedSearch;
    if (sharedFilters.paisId) params.paisId = sharedFilters.paisId;
    api.get('/arbitros/sin-partidos', { params })
      .then(r => {
        const rows = r.data.data || [];
        setData(rows);
        onCountChange('sin-partidos', rows.length);
      })
      .catch(() => { setData([]); onCountChange('sin-partidos', 0); })
      .finally(() => setLoading(false));
  }, [sharedSearch, sharedFilters, onCountChange]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setMobilePage(1); }, [data]);

  const columns = [
    {
      title: 'Árbitro', key: 'nombre',
      render: (_, r) => (
        <span>
          <strong>{(r.PersonaApellido || '').toUpperCase()}</strong>
          {r.PersonaNombre ? `, ${r.PersonaNombre}` : ''}
        </span>
      )
    },
    {
      title: 'País', key: 'pais', width: 170,
      render: (_, r) => (
        <Space size="small">
          {r.PaisBanderaUrl && <img src={r.PaisBanderaUrl} alt="" style={{ width: 24, height: 16, objectFit: 'cover' }} />}
          <span>{r.PaisNombre}</span>
        </Space>
      )
    },
    ...(canEdit ? [{
      title: '', key: 'actions', width: 80,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(r)} />
        </Space>
      )
    }] : [])
  ];

  if (isMobile) {
    const pageData = data.slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE);
    return (
      <div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : pageData.map((record) => (
          <div
            key={record.JuezId}
            style={{ marginBottom: 8, borderRadius: 10, background: '#fff', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', minHeight: 64 }}
            onClick={() => navigate(`/arbitros/${record.JuezId}`)}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 8 }}>
                {(record.PersonaApellido || '').toUpperCase()}{record.PersonaNombre ? `, ${record.PersonaNombre}` : ''}
              </div>
              {record.PaisBanderaUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <img src={record.PaisBanderaUrl} alt="" style={{ width: 24, height: 16, objectFit: 'cover', flexShrink: 0 }} />
                </div>
              )}
            </div>
          </div>
        ))}
        <Pagination
          current={mobilePage}
          pageSize={MOBILE_PAGE_SIZE}
          total={data.length}
          onChange={(p) => setMobilePage(p)}
          size="small"
          showLessItems
          style={{ textAlign: 'center', marginTop: 8 }}
        />
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="JuezId"
      loading={loading}
      pagination={{ pageSize: 50, showSizeChanger: true }}
      rowClassName="clickable-row"
      onRow={(r) => ({
        onClick: (e) => { if (e.target.closest('.ant-btn')) return; navigate(`/arbitros/${r.JuezId}`); }
      })}
    />
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const Arbitros = () => {
  const { canEdit } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const hasUrlFilterParams = [...searchParams.keys()].some(k => PARAM_TO_FILTER_ARB[k] || k === 'bt');
  const saved = hasUrlFilterParams ? null : loadState();

  const [searchText, setSearchText]               = useState(hasUrlFilterParams ? (searchParams.get('bt') || '') : (saved?.searchText ?? ''));
  const [debouncedSearch, setDebouncedSearch]     = useState(hasUrlFilterParams ? (searchParams.get('bt') || '') : (saved?.searchText ?? ''));
  const [filters, setFilters]                     = useState(hasUrlFilterParams ? paramsToFilters_Arb(searchParams) : { ...DEFAULT_FILTERS, ...(saved?.filters || {}) });
  const [filtrosModalOpen, setFiltrosModalOpen]   = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [equipoPaisFilter, setEquipoPaisFilter]   = useState(null);
  const [tabCounts, setTabCounts]                 = useState({});
  const [refreshKey, setRefreshKey]               = useState(0);

  // Data for selects
  const [paisOptions, setPaisOptions]       = useState([]);
  const [ciudades, setCiudades]             = useState([]);
  const [equipoOptions, setEquipoOptions]   = useState([]);
  const [nombresCampeonatoRaw, setNombresC] = useState([]);

  // CRUD modals
  const [createOpen, setCreateOpen]       = useState(false);
  const [createSaving, setCreateSaving]   = useState(false);
  const [editOpen, setEditOpen]           = useState(false);
  const [editSaving, setEditSaving]       = useState(false);
  const [editPersonaData, setEditPersonaData] = useState(null);
  const [editRecord, setEditRecord]       = useState(null);


  // Debounce search
  const searchTimer = useRef(null);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchText]);

  // Persist state
  useEffect(() => { saveState({ searchText, filters }); }, [searchText, filters]);

  // Sync filters to URL params
  useEffect(() => {
    setSearchParams(filtersToParams_Arb(filters, searchText), { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchText]);

  useEffect(() => {
    services.paises.getAll({ limit: 300 }).then(res => {
      setPaisOptions([...(res?.data?.data || [])].sort((a, b) => a.PaisNombre.localeCompare(b.PaisNombre)));
    }).catch(() => {});
    api.get('/arbitros/equipos').then(r => setEquipoOptions(r.data.data || [])).catch(() => {});
    services.nombresCampeonato.getAll({ limit: 200, CampeonatoAmistoso: 0 }).then(res => setNombresC(res?.data?.data || [])).catch(() => {});
  }, []);

  const handleCountChange = useCallback((tabKey, count) => {
    setTabCounts(prev => ({ ...prev, [tabKey]: count }));
  }, []);

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const clearAllFilters = () => {
    setSearchText('');
    setDebouncedSearch('');
    setFilters({ ...DEFAULT_FILTERS });
  };

  const openFiltrosModal = () => {
    const anioActive = filters.anioMode === 'unico' ? !!filters.anioUnico : (filters.anioDesde !== MIN_YEAR || filters.anioHasta !== CURRENT_YEAR);
    setCollapsedSections({
      pais:       !filters.paisId,
      equipo:     !filters.equipoId && !equipoPaisFilter,
      anios:      !anioActive,
      tipo:       filters.soloOficiales,   // collapsed when default (true)
      campeonato: !filters.campeonatoNombreId,
    });
    setFiltrosModalOpen(true);
  };

  // ── CRUD handlers ──────────────────────────────────────────────────────────

  const handleCreate = async (values) => {
    setCreateSaving(true);
    try {
      const persRes = await services.personas.create(values);
      const persona = persRes.data;
      await services.jueces.create({ PersonaId: persona.PersonaId });
      message.success('Árbitro creado');
      setCreateOpen(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      message.error(e?.response?.data?.error || 'Error al crear');
    } finally { setCreateSaving(false); }
  };

  const handleEditOpen = async (record) => {
    try {
      // Refrescar lista de países y ciudades antes de abrir el modal
      const [paisesRes, ciudadesRes] = await Promise.all([
        services.paises.getAll({ limit: 300 }),
        services.ciudades.getAll({ limit: 500, PaisId: record.PaisId || undefined })
      ]);
      setPaisOptions([...(paisesRes?.data?.data || [])].sort((a, b) => a.PaisNombre.localeCompare(b.PaisNombre)));
      setCiudades(ciudadesRes?.data?.data || []);

      const res = await services.personas.getById(record.PersonaId);
      const p = res.data;
      setEditPersonaData({
        PersonaNombre: p.PersonaNombre || '',
        PersonaApellido: p.PersonaApellido || '',
        PersonaNombreCompleto: p.PersonaNombreCompleto || '',
        PersonaApodo: p.PersonaApodo || '',
        PaisId: p.PaisId || null,
        CiudadId: p.CiudadId || null,
        PersonaFecNac: p.PersonaFecNac,
        PersonaFecDef: p.PersonaFecDef,
      });
      setEditRecord(record);
      setEditOpen(true);
    } catch { message.error('Error al cargar datos del árbitro'); }
  };

  const handleEdit = async (values) => {
    setEditSaving(true);
    try {
      await services.personas.update(editRecord.PersonaId, values);
      message.success('Árbitro actualizado');
      setEditOpen(false);
      setEditPersonaData(null);
      setEditRecord(null);
      setRefreshKey(k => k + 1);
    } catch (e) {
      message.error(e?.response?.data?.error || 'Error al guardar');
    } finally { setEditSaving(false); }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `¿Eliminar árbitro "${formatName(record)}"?`,
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar', okType: 'danger', cancelText: 'Cancelar',
      onOk: async () => {
        await services.jueces.delete(record.JuezId);
        message.success('Árbitro eliminado');
        setRefreshKey(k => k + 1);
      }
    });
  };


  // ── SectionPanel ───────────────────────────────────────────────────────────

  const toggleSection = (key) => setCollapsedSections(s => ({ ...s, [key]: !s[key] }));

  const SectionPanel = ({ sectionKey, title, isActive, onClear, children }) => (
    <div style={{ marginBottom: 8, borderRadius: 8, border: `1px solid ${isActive ? '#91caff' : '#f0f0f0'}`, overflow: 'hidden', ...(isMobile ? { paddingBottom: 10 } : {}) }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: isMobile ? '8px 12px' : '10px 14px', minHeight: 42, background: isActive ? '#e6f4ff' : '#fafafa', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => toggleSection(sectionKey)}
      >
        {collapsedSections[sectionKey]
          ? <RightOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
          : <DownOutlined  style={{ fontSize: 11, color: '#8c8c8c' }} />}
        <span style={{ fontWeight: 600, fontSize: isMobile ? 15 : 13, color: '#262626' }}>{title}</span>
        <div style={{ flex: 1 }} />
        {isActive && (
          <Button size="small" type="text" icon={<ClearOutlined />}
            style={{ color: '#1677ff', fontSize: 12, flexShrink: 0 }}
            onClick={(e) => { e.stopPropagation(); onClear(); }}>
            Limpiar
          </Button>
        )}
      </div>
      {!collapsedSections[sectionKey] && (
        <div style={{ padding: isMobile ? '8px 12px' : '12px 14px', ...(isMobile ? {} : { overflowX: 'auto' }) }}>{children}</div>
      )}
    </div>
  );

  // ── Derived UI state ───────────────────────────────────────────────────────

  const aniosDesde = useMemo(() =>
    Array.from({ length: CURRENT_YEAR - MIN_YEAR + 1 }, (_, i) => MIN_YEAR + i).map(y => ({ value: y, label: String(y) })),
    []
  );
  const aniosHasta = useMemo(() => [...aniosDesde].reverse(), [aniosDesde]);

  const paisFilterOptions = useMemo(() => [
    { value: 'extranjeros', label: 'Extranjeros', flagUrl: null },
    ...paisOptions.map(p => ({ value: String(p.PaisId), label: p.PaisNombre, flagUrl: p.PaisBanderaUrl })),
  ], [paisOptions]);

  const campeonatoOptions = useMemo(() => [
    { value: 'internacionales', label: '🌐 Internacionales' },
    ...(nombresCampeonatoRaw || []).map(nc => ({ value: String(nc.NombreCampeonatoId), label: nc.NombreCampeonato }))
  ], [nombresCampeonatoRaw]);

  const activeFilterCount = [
    !!filters.paisId,
    filters.anioMode === 'unico' ? !!filters.anioUnico : (filters.anioDesde !== MIN_YEAR || filters.anioHasta !== CURRENT_YEAR),
    !!filters.equipoId || !!equipoPaisFilter,
    !filters.soloOficiales,
    !!filters.campeonatoNombreId,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0 || !!searchText;

  // Filter description
  const filterParts = [];
  if (filters.paisId === 'extranjeros') filterParts.push('Extranjeros');
  else if (filters.paisId) {
    const p = paisOptions.find(x => String(x.PaisId) === String(filters.paisId));
    if (p) filterParts.push(p.PaisNombre);
  }
  if (filters.anioMode === 'unico' && filters.anioUnico) filterParts.push(`Año ${filters.anioUnico}`);
  else if (filters.anioDesde !== MIN_YEAR || filters.anioHasta !== CURRENT_YEAR) filterParts.push(`${filters.anioDesde} – ${filters.anioHasta}`);
  if (filters.equipoId) {
    const eq = equipoOptions.find(e => String(e.EquipoId) === String(filters.equipoId));
    if (eq) filterParts.push(eq.EquipoNombre);
  }
  if (!filters.soloOficiales) filterParts.push('Con amistosos');
  if (filters.campeonatoNombreId === 'internacionales') filterParts.push('Internacionales');
  else if (filters.campeonatoNombreId) {
    const nc = nombresCampeonatoRaw.find(x => String(x.NombreCampeonatoId) === String(filters.campeonatoNombreId));
    if (nc) filterParts.push(nc.NombreCampeonato);
  }
  const filterDesc = filterParts.join(' · ');

  const filteredEquipoOptions = useMemo(() =>
    equipoPaisFilter
      ? equipoOptions.filter(e => String(e.EquipoPaisId) === String(equipoPaisFilter))
      : equipoOptions,
    [equipoOptions, equipoPaisFilter]
  );

  // Order dropdown
  const orderDropdownMenu = {
    items: [
      { key: 'PJ',     label: 'PJ',     style: { minHeight: 42, display: 'flex', alignItems: 'center' } },
      { key: 'nombre', label: 'Nombre', style: { minHeight: 42, display: 'flex', alignItems: 'center' } },
    ],
    selectedKeys: [filters.orderBy],
    onClick: ({ key }) => updateFilter('orderBy', key),
  };

  // Stats bar (right of title)
  const statsBar = (
    <div style={{ display: 'flex', alignItems: 'center', background: '#f5f5f5', borderRadius: 8, padding: '6px 16px', fontSize: 13 }}>
      {TABS.map((tab, i) => (
        <React.Fragment key={tab.key}>
          {i > 0 && <div style={{ width: 1, height: 28, background: '#e0e0e0', margin: '0 8px' }} />}
          <Tooltip title={tab.label}>
            <div style={{ textAlign: 'center', cursor: 'default' }}>
              <div style={{ fontSize: 11, color: '#888', lineHeight: 1.2 }}>{tab.statLabel}</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#262626', lineHeight: 1.4 }}>
                {tabCounts[tab.key] ?? '–'}
              </div>
            </div>
          </Tooltip>
        </React.Fragment>
      ))}
    </div>
  );

  // Build tab items — forceRender so all grids load immediately
  const tabItems = [
    ...TABS.map(tab => {
      const count = tabCounts[tab.key];
      if (count === 0 && hasActiveFilters) return null;
      return {
        key: tab.key,
        label: tab.label,
        forceRender: true,
        children: (
          <ArbitrosTab
            tabKey={tab.key}
            tipos={tab.tipos}
            extraColumns={tab.key === '1' ? principalExtraColumns : []}
            refreshKey={refreshKey}
            onEdit={handleEditOpen}
            canEdit={canEdit}
            sharedFilters={filters}
            sharedSearch={debouncedSearch}
            onCountChange={handleCountChange}
            isMobile={isMobile}
          />
        )
      };
    }).filter(Boolean),
    ...(canEdit ? [{
      key: 'sin-partidos',
      label: 'Sin Partidos',
      forceRender: true,
      children: (
        <SinPartidosTab
          onEdit={handleEditOpen}
          onDelete={handleDelete}
          refreshKey={refreshKey}
          canEdit={canEdit}
          sharedSearch={debouncedSearch}
          sharedFilters={filters}
          onCountChange={handleCountChange}
          isMobile={isMobile}
        />
      )
    }] : [])
  ];

  return (
    <div>
      <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#614700' }}>
        Algunos datos de los árbitros pueden no estar completamente actualizados, estamos en proceso de completarlos.
      </div>
      {/* Header */}
      {isMobile ? (
        <>
          <h2 style={{ margin: 0, textAlign: 'center' }}>Árbitros</h2>
          <Divider style={{ margin: '8px 0' }} />
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0 }}>Árbitros</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>{statsBar}</div>
        </div>
      )}

      {/* Add button row */}
      {canEdit && !isMobile && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}>
            Agregar Árbitro
          </Button>
        </div>
      )}
      {!canEdit && <div style={{ marginBottom: 12 }} />}

      {/* Filter row */}
      {isMobile ? (
        <div style={{ marginBottom: 12 }}>
          <Input
            placeholder="Buscar por nombre, apellido, país..."
            prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            size="large"
            style={{ width: '100%', fontSize: 16, marginBottom: 8, height: 42 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Button size="large" icon={<FilterOutlined />} onClick={() => openFiltrosModal()}>
              Filtros
              {activeFilterCount > 0 && <Tag color="blue" style={{ marginLeft: 4 }}>{activeFilterCount}</Tag>}
            </Button>
            {hasActiveFilters && (
              <Button size="large" onClick={clearAllFilters}>Limpiar</Button>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <Dropdown menu={orderDropdownMenu} trigger={['click']}>
                <Button size="large">
                  <Space size={4}>{ORDER_LABELS[filters.orderBy] || 'PJ'}<DownOutlined style={{ fontSize: 10 }} /></Space>
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'nowrap' }}>
          <Input
            placeholder="Buscar por nombre, apellido, país..."
            prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            style={{ width: 300, flexShrink: 0, height: 42 }}
          />
          <Button icon={<FilterOutlined />} onClick={() => openFiltrosModal()}>
            Filtros
            {activeFilterCount > 0 && <Tag color="blue" style={{ marginLeft: 4 }}>{activeFilterCount}</Tag>}
          </Button>
          {filterDesc
            ? <span style={{ fontSize: 12, color: '#666', fontStyle: 'italic', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filterDesc}</span>
            : <div style={{ flex: 1 }} />
          }
          {hasActiveFilters && (
            <Button icon={<ClearOutlined />} onClick={clearAllFilters} style={{ flexShrink: 0 }}>Limpiar</Button>
          )}
          <Dropdown menu={orderDropdownMenu} trigger={['click']}>
            <Button style={{ flexShrink: 0 }}>
              <Space size={4}>{ORDER_LABELS[filters.orderBy] || 'PJ'}<DownOutlined style={{ fontSize: 10 }} /></Space>
            </Button>
          </Dropdown>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultActiveKey="1" items={tabItems} />

      {/* Filtros Modal */}
      <Modal
        title={<span><FilterOutlined style={{ marginRight: 8 }} />Filtros</span>}
        open={filtrosModalOpen}
        onCancel={() => setFiltrosModalOpen(false)}
        closeIcon={isMobile
          ? activeFilterCount > 0
            ? <CheckOutlined style={{ fontSize: 18, color: '#1890ff' }} />
            : <CloseOutlined style={{ fontSize: 18 }} />
          : undefined}
        footer={isMobile
          ? [<Button key="apply" type="primary" style={{ height: 42, width: '100%', fontWeight: 700, color: '#fff' }} onClick={() => setFiltrosModalOpen(false)}>Aplicar</Button>]
          : [
              <Button key="clear" icon={<ClearOutlined />} onClick={() => { setFilters({ ...DEFAULT_FILTERS }); setFiltrosModalOpen(false); }}>Limpiar todo</Button>,
              <Button key="apply" type="primary" onClick={() => setFiltrosModalOpen(false)}>Aplicar</Button>
            ]
        }
        width={isMobile ? '100%' : 560}
        className={isMobile ? 'mobile-filters-modal' : ''}
        style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', paddingBottom: 0 } : { top: 20 }}
        styles={{
          body: { padding: isMobile ? 0 : '12px', ...(isMobile ? {} : { maxHeight: '75vh', overflowY: 'auto' }) },
          content: isMobile ? { borderRadius: 0, padding: '12px', minHeight: '100dvh' } : {}
        }}
        destroyOnClose={false}
      >
        {/* Filtros rápidos */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, padding: isMobile ? '16px 12px 0' : 0 }}>
          <Button
            type="primary"
            onClick={() => { setFilters({ ...DEFAULT_FILTERS }); setFiltrosModalOpen(false); }}
            style={isMobile ? { width: 'calc(50% - 4px)', fontWeight: 700, color: '#fff' } : {}}
          >
            Solo Oficiales
          </Button>
        </div>

        {/* País */}
        <SectionPanel
          sectionKey="pais"
          title="País"
          isActive={!!filters.paisId}
          onClear={() => updateFilter('paisId', null)}
        >
          <Select
            placeholder="Todos los países"
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={filters.paisId}
            onChange={v => updateFilter('paisId', v ?? null)}
            options={paisFilterOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            optionRender={option => (
              <Space size="small">
                {option.data.flagUrl && (
                  <img src={option.data.flagUrl} alt="" style={{ width: 20, height: 14, objectFit: 'cover', flexShrink: 0 }} />
                )}
                {option.label}
              </Space>
            )}
            labelRender={({ value, label }) => {
              const p = paisOptions.find(x => String(x.PaisId) === String(value));
              return (
                <Space size="small">
                  {p?.PaisBanderaUrl && <img src={p.PaisBanderaUrl} alt="" style={{ width: 20, height: 14, objectFit: 'cover' }} />}
                  {label}
                </Space>
              );
            }}
          />
        </SectionPanel>

        {/* Equipo */}
        <SectionPanel
          sectionKey="equipo"
          title="Equipo"
          isActive={!!filters.equipoId || !!equipoPaisFilter}
          onClear={() => { updateFilter('equipoId', null); setEquipoPaisFilter(null); }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {isMobile ? (
              <>
                <div>
                  <Select
                    placeholder="Buscar equipo"
                    value={filters.equipoId || undefined}
                    onChange={(value) => { setEquipoPaisFilter(null); updateFilter('equipoId', value || null); }}
                    style={{ width: '100%' }}
                    options={filteredEquipoOptions.map(eq => ({ value: String(eq.EquipoId), label: eq.EquipoNombre }))}
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      const norm = (t) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                      return norm(option?.label ?? '').includes(norm(input));
                    }}
                    optionRender={(option) => {
                      const escudo = filteredEquipoOptions.find(eq => String(eq.EquipoId) === String(option.value))?.EquipoEscudoUrl;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {escudo
                            ? <img src={escudo} alt={option.label} style={{ width: 20, height: 20, objectFit: 'contain' }} />
                            : <svg width="20" height="20" viewBox="0 0 24 24" fill="#e8e8e8" stroke="#bbb" strokeWidth="1.5" style={{ flexShrink: 0 }}><path d="M12 2L3 6v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-9-4z"/></svg>
                          }
                          <span>{option.label}</span>
                        </div>
                      );
                    }}
                  />
                </div>
                <div>
                  <div style={{ marginTop: 9, marginBottom: 3, fontWeight: 500, fontSize: 12 }}>País</div>
                  <Select
                    placeholder="País"
                    value={equipoPaisFilter || undefined}
                    onChange={(value) => { updateFilter('equipoId', null); setEquipoPaisFilter(value || null); }}
                    style={{ width: '100%' }}
                    options={[{ value: 'extranjeros', label: 'Extranjeros' }, ...paisOptions.map(p => ({ value: String(p.PaisId), label: p.PaisNombre }))]}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    optionRender={(option) => {
                      const pais = paisOptions.find(p => String(p.PaisId) === String(option.value));
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {pais?.PaisBanderaUrl
                            ? <img src={pais.PaisBanderaUrl} alt={pais.PaisNombre} style={{ width: 20, height: 15, objectFit: 'contain', flexShrink: 0 }} />
                            : <div style={{ width: 20, height: 15, flexShrink: 0 }} />
                          }
                          <span>{option.label}</span>
                        </div>
                      );
                    }}
                  />
                </div>
              </>
            ) : (
              <Row gutter={[8, 8]} align="bottom" style={{ flexWrap: 'wrap' }}>
                <Col>
                  <div style={{ marginTop: 9, marginBottom: 3, fontWeight: 500, fontSize: 12 }}><TeamOutlined style={{ marginRight: 4 }} />Equipo</div>
                  <Select
                    placeholder="Buscar equipo"
                    value={filters.equipoId || undefined}
                    onChange={(value) => { setEquipoPaisFilter(null); updateFilter('equipoId', value || null); }}
                    style={{ width: 220 }}
                    options={filteredEquipoOptions.map(eq => ({ value: String(eq.EquipoId), label: eq.EquipoNombre }))}
                    showSearch
                    allowClear
                    filterOption={(input, option) => {
                      const norm = (t) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                      return norm(option?.label ?? '').includes(norm(input));
                    }}
                    optionRender={(option) => {
                      const escudo = filteredEquipoOptions.find(eq => String(eq.EquipoId) === String(option.value))?.EquipoEscudoUrl;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {escudo
                            ? <img src={escudo} alt={option.label} style={{ width: 20, height: 20, objectFit: 'contain' }} />
                            : <svg width="20" height="20" viewBox="0 0 24 24" fill="#e8e8e8" stroke="#bbb" strokeWidth="1.5" style={{ flexShrink: 0 }}><path d="M12 2L3 6v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-9-4z"/></svg>
                          }
                          <span>{option.label}</span>
                        </div>
                      );
                    }}
                  />
                </Col>
                <Col>
                  <div style={{ marginTop: 9, marginBottom: 3, fontWeight: 500, fontSize: 12 }}>País equipo</div>
                  <Select
                    placeholder="País"
                    value={equipoPaisFilter || undefined}
                    onChange={(value) => { updateFilter('equipoId', null); setEquipoPaisFilter(value || null); }}
                    style={{ width: 160 }}
                    options={[{ value: 'extranjeros', label: 'Extranjeros' }, ...paisOptions.map(p => ({ value: String(p.PaisId), label: p.PaisNombre }))]}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    optionRender={(option) => {
                      const pais = paisOptions.find(p => String(p.PaisId) === String(option.value));
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {pais?.PaisBanderaUrl
                            ? <img src={pais.PaisBanderaUrl} alt={pais.PaisNombre} style={{ width: 20, height: 15, objectFit: 'contain', flexShrink: 0 }} />
                            : <div style={{ width: 20, height: 15, flexShrink: 0 }} />
                          }
                          <span>{option.label}</span>
                        </div>
                      );
                    }}
                  />
                </Col>
              </Row>
            )}
          </div>
        </SectionPanel>

        {/* Fecha */}
        <SectionPanel
          sectionKey="anios"
          title="Fecha"
          isActive={filters.anioMode === 'unico' ? !!filters.anioUnico : (filters.anioDesde !== MIN_YEAR || filters.anioHasta !== CURRENT_YEAR)}
          onClear={() => setFilters(f => ({ ...f, anioMode: 'rango', anioDesde: MIN_YEAR, anioHasta: CURRENT_YEAR, anioUnico: null }))}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Radio.Group
              value={filters.anioMode}
              onChange={e => setFilters(f => ({ ...f, anioMode: e.target.value }))}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              {[{ value: 'unico', label: 'Año' }, { value: 'rango', label: 'Rango de años' }].map(opt => (
                <Radio key={opt.value} value={opt.value} style={{ minHeight: isMobile ? 42 : 40, display: 'flex', alignItems: 'center' }}>{opt.label}</Radio>
              ))}
            </Radio.Group>
            {filters.anioMode === 'unico' && (
              <div style={{ marginTop: 9, marginBottom: 10 }}>
                <div style={{ marginBottom: 3, fontWeight: 500, fontSize: 12 }}>Año</div>
                <Select
                  placeholder="Año"
                  value={filters.anioUnico || undefined}
                  onChange={val => updateFilter('anioUnico', val ?? null)}
                  style={{ width: '50%' }}
                  options={aniosHasta}
                  showSearch allowClear
                />
              </div>
            )}
            {filters.anioMode === 'rango' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 9, marginBottom: 10 }}>
                <div style={{ width: '50%' }}>
                  <div style={{ marginBottom: 3, fontWeight: 500, fontSize: 12 }}>Año Desde</div>
                  <Select
                    placeholder="Año desde"
                    value={filters.anioDesde}
                    onChange={val => updateFilter('anioDesde', val ?? MIN_YEAR)}
                    style={{ width: '100%' }}
                    options={aniosDesde}
                    showSearch allowClear
                  />
                </div>
                <div style={{ width: '50%' }}>
                  <div style={{ marginBottom: 3, fontWeight: 500, fontSize: 12 }}>Año Hasta</div>
                  <Select
                    placeholder="Año hasta"
                    value={filters.anioHasta}
                    onChange={val => updateFilter('anioHasta', val ?? CURRENT_YEAR)}
                    style={{ width: '100%' }}
                    options={aniosHasta}
                    showSearch allowClear
                  />
                </div>
              </div>
            )}
          </div>
        </SectionPanel>

        {/* Tipo de Partido */}
        <SectionPanel
          sectionKey="tipo"
          title="Tipo de Partido"
          isActive={!filters.soloOficiales}
          onClear={() => updateFilter('soloOficiales', true)}
        >
          <Checkbox.Group
            value={filters.soloOficiales ? ['O'] : ['O', 'A']}
            onChange={vals => updateFilter('soloOficiales', !vals.includes('A'))}
          >
            <Space direction="vertical" size={4}>
              <Checkbox value="O">Oficiales</Checkbox>
              <Checkbox value="A">Amistosos</Checkbox>
            </Space>
          </Checkbox.Group>
        </SectionPanel>

        {/* Campeonato */}
        <SectionPanel
          sectionKey="campeonato"
          title="Campeonato"
          isActive={!!filters.campeonatoNombreId}
          onClear={() => updateFilter('campeonatoNombreId', null)}
        >
          <Select
            placeholder="Todos los campeonatos"
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={filters.campeonatoNombreId}
            onChange={v => updateFilter('campeonatoNombreId', v ?? null)}
            options={campeonatoOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </SectionPanel>
      </Modal>

      {/* Create Modal */}
      <EditPersonaModal
        visible={createOpen}
        onCancel={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        initialValues={null}
        paises={paisOptions}
        ciudades={ciudades}
        setCiudades={setCiudades}
        saving={createSaving}
        title="Agregar Árbitro"
      />

      {/* Edit Modal */}
      <EditPersonaModal
        visible={editOpen}
        onCancel={() => { setEditOpen(false); setEditPersonaData(null); setEditRecord(null); }}
        onSubmit={handleEdit}
        initialValues={editPersonaData}
        paises={paisOptions}
        ciudades={ciudades}
        setCiudades={setCiudades}
        saving={editSaving}
        title="Editar Árbitro"
      />
    </div>
  );
};

export default Arbitros;
